import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PartialMarksList, ListItem, PartialMarkDisabled } from './MarksArray.style';
import { EditInput, Loader } from 'react-inv-forms';

import marksActions from '../../../../redux/marks/actions';
import { UniqueData, MarksActionTypes } from '../../../../redux/marks/types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Id } from '../../../../interfaces/global';
import { RootState } from '../../../../redux/reducers';
import Moment from 'moment-timezone';
import { Tooltip } from 'antd';
import { DisabledContent } from '../../../../styles/sharedUIElemStyles';

import useDateOperations from '../../../../hooks/useDateOperations';

const { createPartialMark, updatePartialMark, deletePartialMark } = marksActions;

type PartialMarkAction = (data: UniqueData, tabId: number, studentId: Id, refreshCallback: any) => MarksActionTypes;

interface MapDispatchToProps {
  /** akcja utworzenia oceny końcowej */
  createPartialMark: PartialMarkAction;
  /** akcja edycji oceny końcowej */
  updatePartialMark: PartialMarkAction;
  /** akcja usunęcia oceny końcowej */
  deletePartialMark: PartialMarkAction;
}

interface MapStateToProps {
  /** stan ładowania */
  loading: boolean;
  /** aktualny język aplikacji */
  currentLanguage: string;
}

export interface MarksArrayProps extends MapDispatchToProps, MapStateToProps {
  /** informacje o ocenie i formie zajęć, której dotyczy */
  value: PartialMarkData[];
  /** obiekt z danymi, do których powiązana są oceny */
  row: any;
  /** zestaw informacji o zakładce, w której osadzony jest komponent */
  tab: any;
  /** akcja aktualizacji danych */
  refreshCallback?: any;
}
export interface PartialMarkData {
  wpisujacy: {
    pId: number;
    nazwa: string;
  };
  ocena: number;
  dataOceny: string;
  waga: number;
  forma: Forma;
}
export interface Forma {
  kod: string;
  nazwa: string;
}
interface PartialMarksArray {
  data: PartialMarkData[];
}
export interface PartialMarksArrayTableCellTypes extends PartialMarksArray {
  type: 'partial_marks';
}
/**
 * Komponent wystawiania ocen cząstkowych
 *
 */
export const MarksArray = ({
  value,
  row: { marks, details, dostep },
  tab,
  loading,
  createPartialMark,
  updatePartialMark,
  deletePartialMark,
  refreshCallback,
  currentLanguage,
}: MarksArrayProps): React.ReactElement => {
  const { t } = useTranslation();
  const { generateDefaultDate } = useDateOperations();
  const { oceny } = marks;
  Moment.tz.setDefault('Europe/Warsaw');
  const onConfirm = (forma: Forma, ocena: any, value: string, mode: string, date: any, event: Event): void => {
    const middayPLUnix = date.set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).unix();
    const data: UniqueData = {
      kodWydzialu: tab.wydzialZlecajacyKod,
      typKartyPrzedmiotu: marks.kartaTypKod,
      semestrAkademicki: details.semestrAkademicki,
      rokAkademicki: details.rokAkademicki,
      kodPrzedmiotu: details.przedmiotKod.replace(/ /g, '-'),
      kartaPrzedmiotId: marks.kartaOcenId,
      ocena: value.toString(),
      formaZajec: forma.kod,
      obligatoryjnaDataWystawienia: middayPLUnix,
      wagaFormyZajec: ocena.formaWaga,
      typPrzydzialu: tab.typPrzydzialu,
    };
    const studentId = details.studentId;
    ocena.ocena === ''
      ? createPartialMark(data, tab.tabId, studentId, refreshCallback)
      : updatePartialMark(data, tab.tabId, studentId, refreshCallback);
  };
  const onDelete = (forma: Forma, ocena: any): void => {
    const data: UniqueData = {
      kodWydzialu: tab.wydzialZlecajacyKod,
      typKartyPrzedmiotu: marks.kartaTypKod,
      semestrAkademicki: details.semestrAkademicki,
      rokAkademicki: details.rokAkademicki,
      kodPrzedmiotu: details.przedmiotKod.replace(/ /g, '-'),
      kartaPrzedmiotId: marks.kartaOcenId,
      formaZajec: forma.kod,
      wagaFormyZajec: ocena.formaWaga,
      ocena: '-',
      obligatoryjnaDataWystawienia: ocena.dataOcenyTimestamp,
      typPrzydzialu: tab.typPrzydzialu,
    };
    const studentId = details.studentId;
    deletePartialMark(data, tab.tabId, studentId, refreshCallback);
  };
  const generateMarkDescription = useCallback((elem, ocena) => {
    return (
      <>
        {elem.nazwa.charAt(0).toUpperCase()}
        {elem.nazwa.slice(1)} ({ocena ? ocena.formaWaga : 'bd'}%)
        {window.outerWidth <= 1024 ? <br /> : ' '}
        <em>{ocena.prowadzacy.nazwa ? `${ocena.prowadzacy.nazwa}` : null}</em>
        <em>{ocena.dataOcenyTimestamp ? `, ${Moment(ocena.dataOcenyTimestamp, 'X').format('L')}` : null}</em>
      </>
    );
  }, []);
  return (
    <>
      {loading && (
        <DisabledContent>
          <Loader />
        </DisabledContent>
      )}
      <PartialMarksList>
        {value && value.length
          ? value.map((elem: any, index: number) => {
              const ocena: any = oceny.find((ocena: any) => ocena.formaNazwa.toLowerCase() === elem.nazwa.toLowerCase());
              const hasAccessToModify: boolean = (ocena && elem.czyProwadziForme) || dostep.czyKierownik;
              return (
                <ListItem key={index} data-cy={`partialMark_${index}`}>
                  {ocena ? (
                    <>
                      {ocena.formaWaga.length > 0 ? (
                        hasAccessToModify ? (
                          <>
                            <EditInput
                              value={ocena.ocena.length ? ocena.ocena : undefined}
                              onConfirm={onConfirm.bind(null, elem, ocena)}
                              onDelete={onDelete.bind(null, elem, ocena)}
                              isEditable={hasAccessToModify && ocena.formaWaga.length > 0}
                              noMarkText={t('NO_MARK')}
                              confirmText={t('ARE_YOU_SURE')}
                              pickMarkText={t('PICK_MARK')}
                              dateDefaultValue={generateDefaultDate(ocena.dataOcenyTimestamp)}
                              isMarkNumeral={marks.przedmiotTypOceny === 'l'}
                              currentLanguage={currentLanguage}
                            />
                            <div>
                              <span>{generateMarkDescription(elem, ocena)}</span>
                            </div>
                          </>
                        ) : (
                          <Tooltip title={t('NO_RIGHTS_TO_GIVE_MARK')}>
                            <span>
                              <b>{ocena.ocena.length ? ocena.ocena : t('NO_MARK')}</b>&nbsp;
                              {generateMarkDescription(elem, ocena)}
                            </span>
                          </Tooltip>
                        )
                      ) : (
                        <Tooltip title={t('FORMA_NO_GRADES_IMPORTANCE')}>
                          <PartialMarkDisabled>
                            <b>{ocena.ocena.length ? ocena.ocena : t('NO_MARK')}</b>&nbsp;
                            {generateMarkDescription(elem, ocena)}
                          </PartialMarkDisabled>
                        </Tooltip>
                      )}
                    </>
                  ) : (
                    t('NO_MARK')
                  )}
                </ListItem>
              );
            })
          : t('NO_MARK')}
      </PartialMarksList>
    </>
  );
};
const mapStateToProps = (state: RootState): MapStateToProps => ({
  loading: state.Marks.partialMarks.loading,
  currentLanguage: state.AppConfig.currentLanguage,
});

const mapDispatchToProps: MapDispatchToProps = {
  createPartialMark,
  updatePartialMark,
  deletePartialMark,
};

export default compose<any>(connect(mapStateToProps, mapDispatchToProps))(MarksArray);
